// Private Functions / Interfaces

interface RGB {
    r: number
    g: number
    b: number
}
/**
 * Parse a hex color string (#RGB or #RRGGBB) into an { r, g, b } object.
 *
 * Thanks to ChatGPT on this one
 */
function hexToRgb(hex: string): RGB | null {
    // Normalize #RGB to #RRGGBB
    const normalized = hex.replace(
        /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
        (_, r, g, b) => `#${r}${r}${g}${g}${b}${b}`,
    )
    const match = normalized.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i)
    if (!match) return null
    const [, rr, gg, bb] = match
    return {
        r: parseInt(rr, 16),
        g: parseInt(gg, 16),
        b: parseInt(bb, 16),
    }
}

function isOfColor(hex: string, predicate: (rbg: RGB) => boolean): boolean {
    const rgb = hexToRgb(hex)
    if (!rgb) return false
    return predicate(rgb)
}

// Public Exports

/**
 * Returns true if the color is predominantly red.
 *
 * Needs a fairly high threshold. Ran some tests and found the
 * * lowest matching color is #510000
 * * highest matching color is #FD02AC
 */
export function isReddish(hex: string, threshold = 80): boolean {
    return isOfColor(
        hex,
        // Red must be at least `threshold` greater than both green and blue
        ({ r, g, b }) => r > g + threshold && r > b + threshold,
    )
}

/**
 * Returns true if the color is predominantly green.
 */
export function isGreenish(hex: string, threshold = 30): boolean {
    return isOfColor(
        hex,
        // Green must be at least `threshold` greater than both red and blue
        ({ r, g, b }) => g > r + threshold && g > b + threshold,
    )
}

/**
 * Returns true if the color is predominantly yellowish (i.e., red and green both strong).
 *
 * Needs a fairly high threshold. Ran some tests and found the
 * * lowest matching color is #474700
 * * highest matching color is #FCFFB5
 */
export function isYellowish(hex: string, threshold = 70): boolean {
    return isOfColor(hex, ({ r, g, b }) => {
        const redGreenDiff = Math.abs(r - g)
        // Both red and green are high and similar, above blue by threshold but not above each other by threshold
        return (
            r > b + threshold && g > b + threshold && redGreenDiff < threshold
        )
    })
}
